import { Col, Content, FlexboxGrid, Grid, Row, Button, Schema, Text, Modal } from 'rsuite'
import PortalHeader from '../../components/layout/PortalHeader'
import usePlaceNavigate from '../../hooks/usePlaceNavigate'
import PersonDetails from '../../components/form/PersonDetails'
import { useState, useRef, useMemo, useEffect } from 'react'
import apiAccess from '../../api/apiAccess'
import { useSelector, useDispatch } from 'react-redux'
import { selectCurrentIDToken, selectCurrentUserEmail, selectWorkErrorMessage, selectWorkFailed, selectWorkStarted, selectWorkSuccess } from '../../store/slices/auth/authSelectors'
import IconIndicator from '../../components/user-feedback/IconIndicator'
import ResponsiveModal from '../../components/ResponsiveModal'
import { selectConfigExpressRegistrationTypeVerbiage } from '../../store/slices/config/configSelectors'
import pluralize from 'pluralize'
import { updateAccountDetails } from '../../store/slices/auth/authThunks'
import Notice from '../../components/user-feedback/Notice'
import { companyNameValidation, phoneRequiredValidation, phoneValidation, requiredValidation } from '../../common/validation'
import { isNullUndefinedOrEmptyObject } from '../../common/utils'

const UpdateAccount = () => {
    const placeNavigate = usePlaceNavigate();
    const form = useRef();
    const dispatch = useDispatch();

    const [formError, setFormError] = useState({});
    const [formValues, setFormValues] = useState({});
    const [addressComponents, setAddressComponents] = useState({});
    const [currentUserDetails, setCurrentUserDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [successMessageVisible, setSuccessMessageVisible] = useState(false);
    const [errorMessageVisible, setErrorMessageVisible] = useState(false);

    const idToken = useSelector(selectCurrentIDToken);
    const email = useSelector(selectCurrentUserEmail);
    const permitType = useSelector(selectConfigExpressRegistrationTypeVerbiage);

    const updatingDetails = useSelector(selectWorkStarted);
    const updatingDetailsSuccess = useSelector(selectWorkSuccess);
    const updatingDetailsFail = useSelector(selectWorkFailed);
    const updatingDetailsErrorMessage = useSelector(selectWorkErrorMessage);

    const regVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);

    useEffect(() => {
        const getAccountDetails = async () => {
            if (idToken && email) {
                setLoadingDetails(true);
                const userDetails = await apiAccess.getUserDetailsAuthTokenAsync(email, idToken);
                setCurrentUserDetails(userDetails);
            }
        }
        getAccountDetails();
    }, [idToken, email]);

    // set loadingDetails to false here, otherwise there is an issue with it being updated prior to userDetails update being finished
    useEffect(() => {
        if (loadingDetails && !isNullUndefinedOrEmptyObject(currentUserDetails)) {
            setLoadingDetails(false);
        }
    }, [currentUserDetails, loadingDetails])

    const formModel = useMemo(() => {
        return {
            email: '',
            firstName: '',
            lastName: '',
            phone: '',
            companyName: '',
            mailingAddress: '',
        }
    }, []);


    useEffect(() => {
        const addressComponents = currentUserDetails?.misc?.mailingAddressComponents ?? {};
        setAddressComponents(addressComponents);

    }, [currentUserDetails])

    useEffect(() => {
        if (!isNullUndefinedOrEmptyObject(currentUserDetails)) {
            setFormValues({
                email: currentUserDetails?.email ?? '',
                firstName: currentUserDetails?.firstName ?? '',
                lastName: currentUserDetails?.lastName ?? '',
                phone: currentUserDetails?.phone ?? '',
                companyName: currentUserDetails?.companyName ?? '',
                mailingAddress: currentUserDetails?.mailingAddress ?? '',
            });
        }

    }, [currentUserDetails]);

    // Validation
    const { StringType } = Schema.Types;
    const validationModel = {
        // don't need validation for disabled inputs
        phone: phoneRequiredValidation,
        companyName: companyNameValidation,
        mailingAddress: requiredValidation,
    }

    const onUnrestrictedAddressSelected = ({ components }) => {
        setAddressComponents(components);
    }

    const validateForm = () => {
        return form.current.check();
    }

    const openConfirmationModal = () => {
        const isValid = validateForm();

        if (isValid) {
            setConfirmationModalOpen(true);
        }
    }

    const updatePersonalDetails = async () => {
        setSuccessMessageVisible(false);
        setErrorMessageVisible(false);

        const isValid = validateForm();

        if (isValid) {
            const updatedData = { ...formValues, mailingAddressComponents: addressComponents }
            dispatch(updateAccountDetails(currentUserDetails?.userId, updatedData));
        }
    }

    useEffect(() => {
        setConfirmationModalOpen(false);
        if (updatingDetailsSuccess) {
            setSuccessMessageVisible(true);
            const timer = setTimeout(() => {
                setSuccessMessageVisible(false);
            }, 10000); // 10 seconds

            return () => clearTimeout(timer);
        }
        else {
            setSuccessMessageVisible(false);
        }

    }, [updatingDetailsSuccess]);

    useEffect(() => {
        setConfirmationModalOpen(false);
        if (updatingDetailsFail) {
            setErrorMessageVisible(true);
        }
        else {
            setErrorMessageVisible(false);
        }
    }, [updatingDetailsFail]);

    return (
        <>
            <FlexboxGrid justify="space-between">
                <FlexboxGrid.Item colspan={24}>
                    <PortalHeader title={`${regVerbiage} Registration Application`} />
                </FlexboxGrid.Item>
                <FlexboxGrid.Item colspan={24} className='sm-page-padding'>
                    <Content>
                        <Grid className="mt-24">
                            <Row className='mb-24'>
                                <Col xs={24}>
                                    <FlexboxGrid>
                                        <FlexboxGrid.Item style={{ paddingRight: "20px" }}>
                                            <Button appearance="subtle" className='btn-link-underline-hover' onClick={() => { placeNavigate(-1); }}>&lt; Back</Button>
                                        </FlexboxGrid.Item>
                                    </FlexboxGrid>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={24}>
                                    <FlexboxGrid>
                                        <FlexboxGrid.Item>
                                            <Text className="header-text-h3 mb-24">Update Details</Text>
                                        </FlexboxGrid.Item>
                                    </FlexboxGrid>
                                </Col>
                            </Row>
                            {
                                loadingDetails && <IconIndicator />
                            }

                            {
                                !loadingDetails ? (
                                    <>
                                        <Row className='content-section'>
                                            <Col xs={24}>
                                                <div role="grid">
                                                    <Row className='mb-24'>
                                                        <Col xs={24}>
                                                            <Text>Please update your details below. Note that you cannot change some details.  </Text>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col xs={24}>
                                                            <PersonDetails
                                                                formModel={formModel}
                                                                ref={form}
                                                                setFormError={setFormError}
                                                                setFormValues={setFormValues}
                                                                formValues={formValues}
                                                                formError={formError}
                                                                validationModel={
                                                                    validationModel
                                                                }
                                                                emailDisabled={true}
                                                                firstNameDisabled={true}
                                                                lastNameDisabled={true}
                                                                onUnrestrictedAddressLookupChange={(address) => onUnrestrictedAddressSelected(address)}
                                                            />
                                                        </Col>
                                                    </Row>

                                                    {
                                                        updatingDetails && (
                                                            <Row>
                                                                <Col xs={24}>
                                                                    <IconIndicator />
                                                                </Col>
                                                            </Row>
                                                        )
                                                    }

                                                    {
                                                        successMessageVisible && !updatingDetails && (
                                                            <Row className='mb-12'>
                                                                <Col xs={24}>
                                                                    <Notice type='success' message='Your details have been updated successfully.' />
                                                                </Col>
                                                            </Row>
                                                        )
                                                    }

                                                    {
                                                        errorMessageVisible && !updatingDetails && (
                                                            <Row className='mb-12'>
                                                                <Col xs={24}>
                                                                    <Notice message={updatingDetailsErrorMessage} />
                                                                </Col>
                                                            </Row>
                                                        )
                                                    }
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col xs={24} className="mt-16">
                                                <Button
                                                    className='btn-primary sm-100-percent'
                                                    onClick={() => { openConfirmationModal(); }}>Update Details</Button>
                                            </Col>
                                        </Row>
                                    </>
                                ) : null
                            }
                        </Grid>
                    </Content>
                </FlexboxGrid.Item>
            </FlexboxGrid>
            <ResponsiveModal isOpen={confirmationModalOpen} onClose={() => { setConfirmationModalOpen(false) }} ariaLabel="update-account-confirmation">
                <Modal.Header className='ph-8 pt-8'>
                    <Modal.Title as="h2" className="mt-8">Confirmation of Personal Information Update</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: "10px" }}>
                    <Grid fluid>
                        <Row>
                            <Col xs={24}>
                                <Text className="mb-12">
                                    Please note that any details updated here will apply to all {pluralize(permitType).toLocaleLowerCase()} associated with this account.
                                    If you do not wish to update all {pluralize(permitType).toLocaleLowerCase()} with these details, please hit Cancel.
                                </Text>
                                <Text>
                                    This process may take a bit of time depending on how many {pluralize(permitType).toLocaleLowerCase()} are associated with your account.
                                </Text>
                            </Col>
                        </Row>
                        {
                            updatingDetails && (
                                <Row>
                                    <Col xs={24}>
                                        <IconIndicator />
                                    </Col>
                                </Row>
                            )
                        }
                    </Grid>
                </Modal.Body>
                <Modal.Footer style={{ padding: "10px", textAlign: 'left' }}>
                    <Grid fluid>
                        <Row className="mb-16">
                            <Col xs={24}>
                                <Button
                                    onClick={() => { updatePersonalDetails(); }}
                                    className="btn-primary sm-100-percent"
                                    
                                    disabled={updatingDetails}>
                                    Confirm
                                </Button>
                                <Button onClick={() => { setConfirmationModalOpen(false); }} className="btn-tertiary sm-100-percent">
                                    Cancel
                                </Button>
                            </Col>
                        </Row>
                    </Grid>
                </Modal.Footer>
            </ResponsiveModal>
        </>
    )
}

export default UpdateAccount
